/**
 * useLazyLoad - Custom hook for lazy loading content with IntersectionObserver
 * Based on BurrowTV-LITE-1local template (line ~9978)
 * 
 * Defers rendering content until it's about to enter viewport
 * Improves initial page load and reduces memory usage
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Hook for lazy loading elements
 * @param {object} options - IntersectionObserver options
 * @param {string} options.rootMargin - Margin around viewport (default: '200px')
 * @param {number} options.threshold - Visibility threshold 0-1 (default: 0.01)
 * @param {boolean} options.triggerOnce - Only trigger once (default: true)
 * @returns {object} { ref, isVisible, hasBeenVisible }
 */
export function useLazyLoad(options = {}) {
  const {
    rootMargin = '200px',  // Load 200px before entering viewport
    threshold = 0.01,      // Trigger at 1% visibility
    triggerOnce = true     // Only trigger once, don't re-hide
  } = options;
  
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: show immediately if not supported
      setIsVisible(true);
      setHasBeenVisible(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasBeenVisible(true);
            
            // Disconnect if triggerOnce
            if (triggerOnce && element) {
              observer.unobserve(element);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        rootMargin,
        threshold
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [rootMargin, threshold, triggerOnce]);
  
  return {
    ref: elementRef,
    isVisible,
    hasBeenVisible
  };
}

/**
 * Hook for lazy loading images
 * Similar to useLazyLoad but optimized for images
 * @param {object} options - Configuration options
 * @returns {object} { ref, shouldLoad, isLoaded }
 */
export function useLazyImage(options = {}) {
  const {
    rootMargin = '400px',  // Load images earlier to avoid placeholder flash
    threshold = 0
  } = options;
  
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imageRef = useRef(null);
  
  useEffect(() => {
    const element = imageRef.current;
    if (!element) return;
    
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.unobserve(element);
          }
        });
      },
      {
        rootMargin,
        threshold
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [rootMargin, threshold]);
  
  return {
    ref: imageRef,
    shouldLoad,
    isLoaded,
    onLoad: () => setIsLoaded(true)
  };
}

/**
 * Hook for lazy loading data/API calls
 * Defers data fetching until element is near viewport
 * @param {Function} fetchFunction - Async function to fetch data
 * @param {object} options - Configuration options
 * @returns {object} { ref, data, loading, error, refetch }
 */
export function useLazyData(fetchFunction, options = {}) {
  const {
    rootMargin = '300px',
    threshold = 0,
    enabled = true
  } = options;
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const elementRef = useRef(null);
  const hasFetched = useRef(false);
  
  // Setup intersection observer
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;
    
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      setShouldFetch(true);
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasFetched.current) {
            setShouldFetch(true);
            observer.unobserve(element);
          }
        });
      },
      {
        rootMargin,
        threshold
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [rootMargin, threshold, enabled]);
  
  // Fetch data when shouldFetch becomes true
  useEffect(() => {
    if (!shouldFetch || hasFetched.current || !enabled) return;
    
    const fetchData = async () => {
      try {
        hasFetched.current = true;
        setLoading(true);
        setError(null);
        const result = await fetchFunction();
        setData(result);
      } catch (err) {
        setError(err);
        console.error('[useLazyData] Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [shouldFetch, fetchFunction, enabled]);
  
  // Manual refetch function
  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchFunction();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  
  return {
    ref: elementRef,
    data,
    loading,
    error,
    refetch
  };
}

/**
 * Hook for virtual scrolling / windowing
 * Only renders items that are visible in viewport
 * @param {Array} items - Full array of items
 * @param {object} options - Configuration
 * @returns {object} { containerRef, visibleItems, startIndex, endIndex }
 */
export function useVirtualScroll(items, options = {}) {
  const {
    itemHeight = 200,      // Height of each item in pixels
    overscan = 3,          // Number of items to render above/below viewport
    containerHeight = 600  // Height of scrollable container
  } = options;
  
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const containerRef = useRef(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
      const endIndex = Math.min(
        items.length,
        Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
      );
      
      setVisibleRange({ start: startIndex, end: endIndex });
    };
    
    // Initial calculation
    handleScroll();
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [items.length, itemHeight, overscan, containerHeight]);
  
  const visibleItems = items.slice(visibleRange.start, visibleRange.end);
  
  return {
    containerRef,
    visibleItems,
    startIndex: visibleRange.start,
    endIndex: visibleRange.end,
    totalHeight: items.length * itemHeight,
    offsetY: visibleRange.start * itemHeight
  };
}

export default useLazyLoad;
